'use client'

import {
	STORAGE_ENCRYPTED_SEED,
	STORAGE_ENCRYPTED_SEED_SALT,
	STORAGE_ENCRYPTED_SEED_TYPE,
	STORAGE_PUBLIC_KEY,
	STORAGE_LAST_ACTIVE_AT,
	STORAGE_TAPROOT_ADDRESS,
	STORAGE_ACK_WALLET_WARNING,
	STORAGE_XVERSE_PAYMENT_ADDRESS,
	STORAGE_DID_REGISTER,
	STORAGE_FLOATING_MENU,
	STORAGE_WALLET_TYPE,
	STORAGE_XVERSE_TAPROOT_PUBLIC_KEY,
	STORAGE_QUIT,
	STORAGE_PROFILE,
	API_URL,
	API_MINT_URL,
	INSCRIBE_API_URL,
	STORAGE_AUTH_PSBT,
	STORAGE_AUTH_SESSION,
	TOOL_FEE_ADDRESS
} from '@/utils/constants'
import axios from 'axios'
import { openPasswordModalForGesture } from '@/utils/password-keyboard'
import * as btc from '@scure/btc-signer'
import * as bitcoin from 'bitcoinjs-lib'

const requiredEccMethods = [
	'isXOnlyPoint',
	'xOnlyPointAddTweak',
	'xOnlyPointFromScalar',
	'signSchnorr'
] as const

let eccInitPromise: Promise<void> | null = null

type EccModule = typeof import('tiny-secp256k1')

async function loadAndInitEcc(load: () => Promise<EccModule>): Promise<void> {
	const ecc = await load()
	for (const method of requiredEccMethods) {
		if (typeof ecc[method] !== 'function') {
			throw new Error(
				`tiny-secp256k1 is missing ${method}; Taproot wallet operations are unavailable`
			)
		}
	}

	ecc.__initializeContext?.()
	bitcoin.initEccLib(ecc)
}

export async function ensureEccLib(
	load: () => Promise<EccModule> = () => import('tiny-secp256k1')
): Promise<void> {
	if (typeof window === 'undefined') {
		return
	}

	if (!eccInitPromise) {
		// Never cache a rejected promise. A one-off chunk-load/init failure (a Turbopack
		// alias glitch, a stale chunk after a deploy, a transient network error) must not
		// permanently brick every buy/sign for the rest of the session. Reset on failure
		// so the next call retries instead of replaying the cached rejection forever.
		eccInitPromise = loadAndInitEcc(load).catch((e) => {
			eccInitPromise = null
			throw e
		})
	}

	return eccInitPromise
}

export function __resetEccLibForTests(): void {
	eccInitPromise = null
}

import SafeLocalStorage from '../safe-local-storage'
import { ForceParams } from '@/components/inscription-actions/inscription-action-sell'
import sleep from '@/utils/sleep'

/** Bitcoin dust limit for the seller's output; the API refuses listings under it. */
export const LISTING_MIN_PRICE_SATS = 546
import { reportWalletError } from '@/utils/report-error'
import { runExternalSigningRequest } from './run-external-signing-request'
import XverseWallet from './xverse'
import { assertChainLinks, linkChildToSignedParent, txidOfRawTx } from './chain'
import { extractFinalizedPsbtHex, inputTxidHex, parsePsbtTransaction } from './psbt'
import UnisatWallet from './unisat'
import HiroWallet from './hiro'
import DogelabsWallet from './dogelabs'
import PhantomWallet from './phantom'
import NintondoWallet from './nintondo'
import OKXWallet from './okx'
import OYLWallet from './oyl'
import TapWallet from './tap'
import WalletSettings from './settings'
import event from '@/utils/event'
import featureFlags from '@/utils/feature-flags'
import { WalletProvider } from '@/utils/types/wallet'
import { assertLegacyPurchaseBlocked } from '@/utils/market/secure-purchase'

type WalletType =
	| 'ordinalswallet'
	| 'unisat'
	| 'xverse'
	| 'hiro'
	| 'dogelabs'
	| 'phantom'
	| 'nintondo'
	| 'okx'
	| 'oyl'
	| 'tap'

export default class Wallet {
	// @ts-ignore
	public static get name(): string {
		return 'Ordinals Wallet'
	}

	public static get connectIcon(): string {
		return '/connect-ordinals-wallet.png'
	}

	public static get enabled(): boolean {
		return true
	}

	public static get capabilities(): string[] {
		return []
	}

	public static getProfile() {
		try {
			return JSON.parse(localStorage.getItem(STORAGE_PROFILE) ?? '{}')
		} catch (_e) {
			return {}
		}
	}

	public static async setProfile(payload: any) {
		try {
			const profile = JSON.parse(localStorage.getItem(STORAGE_PROFILE) ?? '{}')
			localStorage.setItem(STORAGE_PROFILE, JSON.stringify({ ...profile, ...payload }))
		} catch (e) {
			console.log('Error setting profile', e)
		}
	}

	public static async fetchProfile() {
		const address = Wallet.getTaprootAddress()
		if (!address) return
		try {
			const { data } = await axios.post(`${API_URL}/wallet/${address}/profile`)
			// Ignore a late response if the user changed wallets while the request was
			// in flight. Otherwise stale profile data can overwrite the new session.
			if (Wallet.getTaprootAddress() === address) {
				localStorage.setItem(STORAGE_PROFILE, JSON.stringify(data))
			}
		} catch (e) {
			console.log('Error fetching profile', e)
		}
	}

	public static delete() {
		if (Wallet.notDefined()) {
			return
		}

		SafeLocalStorage.removeItem(STORAGE_ENCRYPTED_SEED)
		SafeLocalStorage.removeItem(STORAGE_ENCRYPTED_SEED_SALT)
		SafeLocalStorage.removeItem(STORAGE_ENCRYPTED_SEED_TYPE)
		SafeLocalStorage.removeItem(STORAGE_PUBLIC_KEY)
		SafeLocalStorage.removeItem(STORAGE_LAST_ACTIVE_AT)
		SafeLocalStorage.removeItem(STORAGE_TAPROOT_ADDRESS)
		SafeLocalStorage.removeItem(STORAGE_ACK_WALLET_WARNING)
		SafeLocalStorage.removeItem(STORAGE_FLOATING_MENU)
		SafeLocalStorage.removeItem(STORAGE_DID_REGISTER)
		SafeLocalStorage.removeItem(STORAGE_WALLET_TYPE)
		SafeLocalStorage.removeItem(STORAGE_XVERSE_PAYMENT_ADDRESS)
		SafeLocalStorage.removeItem(STORAGE_XVERSE_TAPROOT_PUBLIC_KEY)
		SafeLocalStorage.removeItem(STORAGE_QUIT)
		SafeLocalStorage.removeItem(STORAGE_PROFILE)
		SafeLocalStorage.removeItem(STORAGE_AUTH_PSBT)
		SafeLocalStorage.removeItem(STORAGE_AUTH_SESSION)
	}

	public static isSet(): boolean {
		if (Wallet.notDefined()) {
			return false
		}

		return !!SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
	}

	public static display_name() {
		const type = Wallet.type()

		if (type === 'ordinalswallet') {
			return 'Ordinals Wallet'
		}

		return Wallet.provider.name
	}

	public static get provider(): WalletProvider {
		const type = Wallet.type()

		if (type === 'ordinalswallet') {
			return Wallet as unknown as WalletProvider
		}

		return Wallet[type] as WalletProvider
	}

	public static providers(): WalletProvider[] {
		// @ts-ignore
		// Connect modal order: OW, then the wallets most of our buyers use
		// (Xverse, UniSat, OKX), Tap Wallet, then the rest.
		return [
			this,
			this.xverse,
			this.unisat,
			this.okx,
			this.tap,
			this.oyl,
			this.phantom,
			this.hiro,
			this.dogelabs,
			this.nintondo
		].filter((e) => e.enabled)
	}

	public static get xverse() {
		return new XverseWallet()
	}

	public static get unisat() {
		return new UnisatWallet()
	}

	public static get phantom() {
		return new PhantomWallet()
	}

	public static get hiro() {
		return new HiroWallet()
	}

	public static get dogelabs() {
		return new DogelabsWallet()
	}

	public static get nintondo() {
		return new NintondoWallet()
	}

	public static get okx() {
		return new OKXWallet()
	}

	public static get oyl() {
		return new OYLWallet()
	}

	public static get tap() {
		return new TapWallet()
	}

	public static get settings() {
		return WalletSettings
	}

	public static async seed(): Promise<{ seed: string; valid: boolean }> {
		// Still inside the tap here (sign message / profile save). After the
		// import below, iOS will not open the keyboard for a programmatic focus.
		openPasswordModalForGesture()
		const { getSeed } = await import('@/utils/password')
		let seed = await getSeed()
		while (!seed.valid) {
			seed = await getSeed()
			await sleep(1000)

			const quit = JSON.parse(localStorage.getItem(STORAGE_QUIT) ?? '{}')
			if (quit.quit) {
				break
			}
		}

		return seed
	}

	public static type(): WalletType {
		if (typeof window === 'undefined') {
			return 'ordinalswallet'
		}

		let type = localStorage.getItem(STORAGE_WALLET_TYPE) as WalletType

		if (!type) {
			return 'ordinalswallet'
		}

		return type
	}

	public static async register(): Promise<void> {
		try {
			if (SafeLocalStorage.getItem(STORAGE_DID_REGISTER) === 'true' || !Wallet.isSet()) {
				return
			}

			const { data } = await axios.post(`${API_URL}/wallet/register`, {
				public_key: Wallet.getPublicKey(),
				address: Wallet.getTaprootAddress(),
				wallet_type: Wallet.type()
			})

			if (data.success) {
				SafeLocalStorage.setItem(STORAGE_DID_REGISTER, 'true')
				Wallet.settings.init()
			}
		} catch (e) {
			console.log(e)
		}
	}

	public static async signInscription(inscription_id: string): Promise<String> {
		const type = Wallet.type()
		let signature = ''

		if (type === 'ordinalswallet') {
			const seed = await Wallet.seed()
			if (!seed.valid) {
				return undefined as unknown as String
			}
			const { signMessage } = await import('@/utils/bitcoin')
			signature = await signMessage(seed.seed, inscription_id)
		} else if (Wallet.provider.capabilities.includes('payments-wallet')) {
			// Parallelize: the import + dummy-PSBT build is a no-op side effect we
			// just wait on; the getEscrow is independent of either.
			const [{ buildDummyPSBT }, escrow_psbt] = await Promise.all([
				import('@/utils/bitcoin'),
				Wallet.getEscrow(inscription_id, 1e8 * 21000000, {})
			])
			await buildDummyPSBT(Wallet.taprootPaymentPublicKey())
			signature = await Wallet.provider.signEscrow(escrow_psbt)
		} else {
			const { buildDummyPSBT } = await import('@/utils/bitcoin')
			const psbt = await buildDummyPSBT(Wallet.getPublicKey())
			signature = await Wallet.provider.signInscribe(psbt)
		}

		return signature
	}

	public static async dummyPsbt(_inscription_id: string): Promise<string> {
		const { dummyPsbt } = await import('@/utils/bitcoin')
		const psbt = await dummyPsbt(Wallet.getPublicKey())
		return psbt
	}

	public static async acceptOffer(escrow_id: string): Promise<void> {
		const { data } = await axios.post(`${API_URL}/market/offers/build-accept`, { escrow_id })
		const { psbt } = await Wallet.signPSBT(data.psbt)
		await axios.post(`${API_URL}/market/offers/accept`, { psbt, escrow_id })
	}

	public static async rejectOffer(inscription_id: string, escrow_id: string): Promise<void> {
		const escrow_psbt = Wallet.provider.capabilities.includes('payments-wallet')
			? await (Wallet as any).provider.dummyBalancePsbt(inscription_id)
			: await (Wallet as any).provider.dummyPsbt(inscription_id)
		const { psbt } = await Wallet.signPSBT(escrow_psbt)
		await axios.post(`${API_URL}/market/offers/reject`, {
			escrow_id,
			address: Wallet.provider.capabilities.includes('payments-wallet')
				? Wallet.balanceAddress()
				: Wallet.getTaprootAddress(),
			signature: psbt
		})
	}

	public static async cancelOffer(inscription_id: string, escrow_id: string): Promise<void> {
		const escrow_psbt = await (Wallet as any).provider.dummyPsbt(inscription_id)
		const { psbt } = await Wallet.signPSBT(escrow_psbt)
		await axios.post(`${API_URL}/market/offers/cancel`, {
			escrow_id,
			address: Wallet.getTaprootAddress(),
			signature: psbt
		})
	}

	public static async cancelEscrow(inscription_id: string): Promise<void> {
		const type = Wallet.type()
		let signature = ''

		const escrow_psbt = await Wallet.getEscrow(inscription_id, 1e8 * 21000000, {})
		if (type === 'ordinalswallet') {
			const { psbt: data } = await this.signPSBT(escrow_psbt)
			signature = data as any
		} else {
			signature = await Wallet.provider.signEscrow(escrow_psbt)
		}

		await axios.post(`${API_URL}/market/cancel-escrow`, {
			inscription_id,
			signature
		})
	}

	public static async cancelRuneEscrow(outpoint: string): Promise<void> {
		const type = Wallet.type()
		let signature = ''

		const escrow_psbt = await Wallet.getRuneEscrows([outpoint], [1e8 * 21000000])
		if (type === 'ordinalswallet') {
			const { psbt: data } = await this.signPSBT(escrow_psbt)
			signature = data as any
		} else {
			signature = await Wallet.provider.signEscrow(escrow_psbt)
		}

		await axios.post(`${API_URL}/market/cancel-escrow`, {
			outpoint,
			signature
		})
	}

	public static async getTransfers(
		inscription_id: string[],
		to: string,
		fee_rate: number,
		utxos: any[],
		postage?: number,
		change_address?: string
	): Promise<string> {
		const payload: any = {
			inscriptions: inscription_id,
			from: Wallet.getTaprootAddress(),
			to: to,
			fee_rate: fee_rate,
			public_key: Wallet.getPublicKey(),
			utxos: utxos || [],
			...(postage != null && { postage })
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			// @ts-ignore
			payload.public_key = Wallet.balancePublicKey()
			// @ts-ignore
			payload.inscription_address = Wallet.getTaprootAddress()
			// @ts-ignore
			payload.inscription_public_key = Wallet.taprootPaymentPublicKey()
			// @ts-ignore
			if (change_address) payload.change_address = change_address
		}

		const { data } = await axios.post(`${API_URL}/wallet/inscription/send-bulk`, payload)

		return data.psbt
	}

	public static async getTransfer(
		inscription_id: string,
		to: string,
		fee_rate: number,
		params: ForceParams
	): Promise<string> {
		const payload: any = {
			inscription: inscription_id,
			from: Wallet.getTaprootAddress(),
			to: to,
			force_excess_sats: params?.forceExcessSats,
			force_multi_inscriptions: params?.forceMultiInscriptions,
			fee_rate: fee_rate
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			// @ts-ignore
			payload.public_key = Wallet.taprootPaymentPublicKey()
			// @ts-ignore
			payload.payment_public_key = Wallet.balancePublicKey()
		}

		const { data } = await axios.post(`${API_URL}/wallet/inscription/send`, payload)

		return data.psbt
	}

	public static async getRuneEscrows(
		inscription_ids: string[],
		escrow_prices: number[]
	): Promise<string> {
		const payload: any = {
			outpoints: inscription_ids,
			from: Wallet.getTaprootAddress(),
			prices: escrow_prices,
			public_key: Wallet.getPublicKey(),
			receive_address: undefined
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.receive_address = Wallet.balanceAddress()
			payload.public_key = Wallet.taprootPaymentPublicKey()
		}

		if (featureFlags('app::disable_bulk')) {
			const id = inscription_ids[0]
			const price = escrow_prices[0]
			payload.outpoints = [id]
			payload.prices = [price]
		}

		const { data } = await axios.post(`${API_URL}/wallet/escrow-bulk`, payload)

		return data.psbt
	}

	public static async getEscrows(
		inscription_ids: string[],
		escrow_prices: number[],
		options?: { forceBulk?: boolean }
	): Promise<string | string[]> {
		// The API skips any listing whose seller output would be dust, after
		// the wallet has already signed. Refuse here so nothing is signed.
		const tooLow = escrow_prices.findIndex((p) => !Number.isFinite(p) || p < LISTING_MIN_PRICE_SATS)
		if (tooLow >= 0) {
			throw new Error(
				`Price for ${escrow_prices.length > 1 ? `item ${tooLow + 1}` : 'this item'} is ${
					escrow_prices[tooLow] || 0
				} sats; the minimum listing price is ${LISTING_MIN_PRICE_SATS} sats.`
			)
		}
		const payload: any = {
			inscriptions: inscription_ids,
			from: Wallet.getTaprootAddress(),
			prices: escrow_prices,
			public_key: Wallet.getPublicKey(),
			receive_address: undefined
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.receive_address = Wallet.balanceAddress()
			payload.public_key = Wallet.taprootPaymentPublicKey()
		}

		if (featureFlags('app::disable_bulk') && !(options as any).forceBulk) {
			const id = inscription_ids[0]
			const price = escrow_prices[0]
			payload.inscriptions = [id]
			payload.prices = [price]
		}

		const { data } = await axios.post(`${API_URL}/wallet/escrow-bulk`, payload)

		if ((options as any).forceBulk) {
			const psbts = []
			const initialTx = btc.Transaction.fromPSBT(Buffer.from(data.psbt, 'hex'))

			// @ts-ignore
			for (let i = 2; i < initialTx.inputs.length; i++) {
				const tx = btc.Transaction.fromPSBT(Buffer.from(data.psbt, 'hex'))
				// @ts-ignore
				tx.inputs[2] = tx.inputs[i]
				// @ts-ignore
				tx.outputs[2] = tx.outputs[i]
				// @ts-ignore
				tx.outputs = tx.outputs.slice(0, 3)
				// @ts-ignore
				tx.inputs = tx.inputs.slice(0, 3)

				psbts.push(Buffer.from(tx.toPSBT()).toString('hex'))
			}

			return psbts
		}

		return data.psbt
	}

	public static async getSigEscrow(
		inscription_id: string,
		escrow_price: number,
		params: ForceParams
	): Promise<string> {
		const payload: any = {
			inscription: inscription_id,
			from: Wallet.getTaprootAddress(),
			price: escrow_price,
			force_excess_sats: params?.forceExcessSats,
			force_multi_inscriptions: params?.forceMultiInscriptions,
			//dummy: Wallet.type() !== 'xverse',
			dummy: false,
			public_key: Wallet.getPublicKey(),
			receive_address: undefined
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.receive_address = Wallet.balanceAddress()
			payload.from = Wallet.balanceAddress()
			//payload.public_key = Wallet.taprootPaymentPublicKey()
		}

		const { data } = await axios.post(`${API_URL}/wallet/escrow`, payload)
		return data.psbt
	}

	public static async getEscrow(
		inscription_id: string,
		escrow_price: number,
		params: ForceParams
	): Promise<string> {
		const payload: any = {
			inscription: inscription_id,
			from: Wallet.getTaprootAddress(),
			price: escrow_price,
			force_excess_sats: params?.forceExcessSats,
			force_multi_inscriptions: params?.forceMultiInscriptions,
			//dummy: Wallet.type() !== 'xverse',
			dummy: false,
			public_key: Wallet.getPublicKey(),
			receive_address: undefined
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.receive_address = Wallet.balanceAddress()
			payload.public_key = Wallet.taprootPaymentPublicKey()
		}

		const { data } = await axios.post(`${API_URL}/wallet/escrow`, payload)
		return data.psbt
	}

	public static getMarketFee(psbt_hex: string): number {
		const feeOutputIndex = 3
		try {
			let tx = btc.Transaction.fromPSBT(Buffer.from(psbt_hex, 'hex'))
			const feeOutput = tx.getOutput(feeOutputIndex)

			return Number(feeOutput?.amount || 0)
		} catch (_e) {
			// @ts-ignore
			const network = bitcoin.networks.mainnet
			const tx = bitcoin.Psbt.fromHex(psbt_hex, { network })
			const feeOutput = tx.txOutputs[feeOutputIndex]

			return feeOutput?.value || 0
		}
	}

	/**
	 * Return the total amount (in sats) paid to a specific address in the PSBT outputs.
	 */
	public static getOutputAmountToAddress(psbt_hex: string, address: string): number {
		if (!address) {
			return 0
		}

		try {
			const script = bitcoin.address.toOutputScript(address)

			let tx = btc.Transaction.fromPSBT(Buffer.from(psbt_hex, 'hex'))
			let total = 0
			// @ts-ignore
			tx.outputs.forEach((o) => {
				// @ts-ignore
				if (o.script && Buffer.from(o.script).equals(script)) {
					total += Number(o.amount || 0)
				}
			})
			return total
		} catch (_e) {
			// @ts-ignore
			const network = bitcoin.networks.mainnet
			const tx = bitcoin.Psbt.fromHex(psbt_hex, { network })
			const script = bitcoin.address.toOutputScript(address)
			let total = 0
			tx.txOutputs.forEach((o) => {
				if (o.script && Buffer.from(o.script).equals(script)) {
					total += o.value || 0
				}
			})
			return total
		}
	}

	/**
	 * Return the total amount (in sats) paid to any of the given addresses in the PSBT outputs.
	 */
	public static getOutputsAmountToAddresses(psbt_hex: string, addresses: string[]): number {
		if (!addresses || addresses.length === 0) {
			return 0
		}
		let sum = 0
		for (const addr of addresses) {
			if (!addr) continue
			sum += Wallet.getOutputAmountToAddress(psbt_hex, addr)
		}
		return sum
	}

	public static getRuneFee(psbt_hex: string): number {
		const feeOutputIndex = 2
		try {
			let tx = btc.Transaction.fromPSBT(Buffer.from(psbt_hex, 'hex'))
			const feeOutput = tx.getOutput(feeOutputIndex)

			return Number(feeOutput?.amount)
		} catch (_e) {
			// @ts-ignore
			const network = bitcoin.networks.mainnet
			const tx = bitcoin.Psbt.fromHex(psbt_hex, { network })
			const feeOutput = tx.txOutputs[feeOutputIndex]

			return feeOutput.value
		}
	}

	public static async getRebarFee(psbt_hex: string): Promise<number> {
		const { data } = await axios.get(`${API_URL}/rebar/info`)
		const script = bitcoin.address.toOutputScript(data.payment.p2wpkh)

		let tx = btc.Transaction.fromPSBT(Buffer.from(psbt_hex, 'hex'))

		let fee = 0

		// @ts-ignore
		tx.outputs.forEach((e) => {
			if (e.script.equals(script)) {
				fee = Number(e.amount)
			}
		})

		return fee
	}

	public static getServiceFee(psbt_hex: string): number {
		try {
			// @ts-ignore
			const script = bitcoin.address.toOutputScript(TOOL_FEE_ADDRESS)
			let tx = btc.Transaction.fromPSBT(Buffer.from(psbt_hex, 'hex'))
			let fee = 0
			// @ts-ignore
			tx.outputs.forEach((e) => {
				if (e.script.equals(script)) {
					fee += Number(e.amount)
				}
			})
			return fee
		} catch {
			return 0
		}
	}

	public static getPSBTOutputValue(psbt_hex: string, address: string): number {
		try {
			// @ts-ignore
			const script = bitcoin.address.toOutputScript(address)
			let tx = btc.Transaction.fromPSBT(Buffer.from(psbt_hex, 'hex'))
			let total = 0
			// @ts-ignore
			tx.outputs.forEach((e) => {
				if (e.script && script && e.script.equals(script)) {
					total += Number(e.amount)
				}
			})
			return total
		} catch {
			return 0
		}
	}

	public static getPSBTFee(psbt_hex: string): bigint {
		try {
			try {
				let tx = btc.Transaction.fromPSBT(Buffer.from(psbt_hex, 'hex'))

				let input_sats = BigInt(0)
				let output_sats = BigInt(0)

				// @ts-ignore
				tx.inputs.forEach((e) => {
					if (e.witnessUtxo) {
						input_sats = input_sats + e?.witnessUtxo?.amount
					} else if (e.nonWitnessUtxo) {
						input_sats = input_sats + e.nonWitnessUtxo.outputs[e.index].amount
					}
				})

				// @ts-ignore
				tx.outputs.forEach((e) => {
					output_sats = output_sats + e?.amount
				})

				const fee = input_sats - output_sats

				return fee
			} catch (_e) {
				// @ts-ignore
				const network = bitcoin.networks.mainnet
				const tx = bitcoin.Psbt.fromHex(psbt_hex, { network })

				let input_sats = BigInt(0)
				let output_sats = BigInt(0)

				tx.data.inputs.forEach((e) => {
					if (e.witnessUtxo) {
						input_sats = input_sats + BigInt(e?.witnessUtxo?.value)
					}

					if (e.nonWitnessUtxo) {
						//let foo = btc;
						//let prev_tx = bitcoin.Transaction.fromBuffer(e.nonWitnessUtxo);
						//input_sats = input_sats + BigInt(prev_tx.outputs[])
					}
				})

				tx.txOutputs.forEach((e) => {
					output_sats = output_sats + BigInt(e.value)
				})

				const fee = input_sats - output_sats
				return fee
			}
		} catch (e) {
			console.log(e)
			return BigInt(0)
		}
	}

	public static async getOffer(
		inscription_id: string,
		price: number,
		fee_rate: number
	): Promise<{ psbt: string; fee: bigint; market_fee: number }> {
		const payload: any = {
			inscription: inscription_id,
			price,
			fee_rate,
			to: undefined,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey()
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.to = Wallet.getTaprootAddress()
		}

		const { data } = await axios.post(`${API_URL}/wallet/offer`, payload)

		const fee = Wallet.getPSBTFee(data.psbt)
		const market_fee = Wallet.getMarketFee(data.psbt)

		return { psbt: data.psbt, fee, market_fee }
	}

	public static async getRuneMint(
		id: string,
		count: number,
		fee_rate: number
	): Promise<{ mints: string[]; setup: string; fee: bigint }> {
		const payload: any = {
			id,
			count,
			fee_rate,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			to: null
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.to = Wallet.getTaprootAddress()
		}

		const { data } = await axios.post(`${API_URL}/rune/mint`, payload)
		const network_fee = Wallet.getPSBTFee(data.setup)

		let tx = btc.Transaction.fromPSBT(Buffer.from(data.setup, 'hex'))
		let output_sats = BigInt(0)

		// @ts-ignore
		tx.outputs.forEach((e, i) => {
			// @ts-ignore
			if (i < tx.outputs.length - 1) {
				output_sats = output_sats + e?.amount
			}
		})

		return { setup: data.setup, mints: data.mints, fee: network_fee + output_sats }
	}

	public static async getRuneEtch(
		p: any,
		fee_rate: number
	): Promise<{ psbt: string; fee: bigint }> {
		const payload: any = {
			...p,
			fee_rate,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey()
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
		}

		const { data } = await axios.post(`${API_URL}/rune/etch`, payload)
		const fee = Wallet.getPSBTFee(data.psbt)

		return { psbt: data.psbt, fee }
	}

	public static async getRuneTransfer(
		edicts: any[],
		outpoints: any[],
		fee_rate: number
	): Promise<{ psbt: string; fee: bigint }> {
		const payload: any = {
			fee_rate,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			outpoint_public_key: null,
			edicts,
			outpoints,
			to: null
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.to = Wallet.getTaprootAddress()
			payload.outpoint_public_key = Wallet.taprootPaymentPublicKey()
		}

		const { data } = await axios.post(`${API_URL}/rune/transfer`, payload)
		const fee = Wallet.getPSBTFee(data.psbt)

		return { psbt: data.psbt, fee }
	}

	public static async getAlkaneTransfer(
		edicts: any[],
		outpoints: any[],
		fee_rate: number
	): Promise<{ psbt: string; fee: bigint }> {
		const payload: any = {
			fee_rate,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			outpoint_public_key: null,
			edicts,
			outpoints,
			to: null
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.to = Wallet.getTaprootAddress()
			payload.outpoint_public_key = Wallet.taprootPaymentPublicKey()
		}

		const { data } = await axios.post(`${API_URL}/alkane/transfer`, payload)
		const fee = Wallet.getPSBTFee(data.psbt)

		return { psbt: data.psbt, fee }
	}

	/// `pools` names the route's pools — one `[poolId, outSlot]` per hop — so
	/// the API builds an OWRouter (2:92934) route. Omitted or empty, the API
	/// falls back to the AMM factory, which is what older clients send.
	public static async getAlkaneSwap(
		path: string[],
		amountIn: bigint,
		minOut: bigint,
		outpoints: [string, number][],
		fee_rate: number,
		pools: [string, boolean][] = []
	): Promise<{ psbt: string; fee: bigint }> {
		const payload: any = {
			fee_rate,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			outpoint_public_key: null,
			path,
			pools,
			sell_outpoints: outpoints
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.outpoint_public_key = Wallet.taprootPaymentPublicKey()
		}

		// amount_in / min_out are u128 on the API — embed them as bare JSON
		// integers (BigInt can exceed Number.MAX_SAFE_INTEGER; JSON.stringify
		// would either throw on BigInt or round a Number).
		const body = JSON.stringify({ ...payload, amount_in: '__AMT__', min_out: '__MIN__' })
			.replace('"__AMT__"', amountIn.toString())
			.replace('"__MIN__"', minOut.toString())

		const { data } = await axios.post(`${API_URL}/alkane/swap`, body, {
			headers: { 'Content-Type': 'application/json' }
		})
		const fee = Wallet.getPSBTFee(data.psbt)

		return { psbt: data.psbt, fee }
	}

	/// frBTC ⇄ frUSD through the CryptoSwap pool (4:1778). Same request
	/// grammar as getAlkaneSwap, but the pool takes no path/pools — the API
	/// builds the two-stone shifter chain that edicts exactly `amountIn`.
	public static async getAlkaneCurveSwap(
		sell: string,
		amountIn: bigint,
		minOut: bigint,
		outpoints: [string, number][],
		fee_rate: number
	): Promise<{ psbt: string; fee: bigint }> {
		const payload: any = {
			fee_rate,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			outpoint_public_key: null,
			sell,
			sell_outpoints: outpoints
		}
		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.outpoint_public_key = Wallet.taprootPaymentPublicKey()
		}
		const body = JSON.stringify({ ...payload, amount_in: '__AMT__', min_out: '__MIN__' })
			.replace('"__AMT__"', amountIn.toString())
			.replace('"__MIN__"', minOut.toString())
		const { data } = await axios.post(`${API_URL}/alkane/curve/swap`, body, {
			headers: { 'Content-Type': 'application/json' }
		})
		const fee = Wallet.getPSBTFee(data.psbt)
		return { psbt: data.psbt, fee }
	}

	/// txid of a signed raw transaction — the chained wrap-swap verifies the
	/// signed ids against the API's pre-sign ids before broadcasting either.
	public static txidOfRawTx(rawtx: string): string {
		return txidOfRawTx(rawtx)
	}

	/// Guard for every chained flow: the child (signed raw tx or PSBT) must
	/// spend the SIGNED parent. Throws `ChainMismatchError` before anything
	/// is broadcast when signing moved the parent's txid (nested-segwit or
	/// legacy funders). See `chain.ts`.
	public static assertChainLinks(
		label: string,
		signedParentRawtx: string,
		childHex: string,
		expectedParentTxids: string[] = []
	): void {
		assertChainLinks(label, signedParentRawtx, childHex, expectedParentTxids)
	}

	/// BTC → any alkane as ONE action: the API builds the wrap AND a swap
	/// that spends the wrap's outputs by its pre-sign txid. Sign both,
	/// verify neither txid moved (a shifted wrap txid would strand the
	/// mint), broadcast wrap first — both confirm in the same block.
	///
	/// `topup` is the partial-wrap variant: `amountSats` is only the
	/// SHORTFALL, the held frBTC carriers ride the swap leg
	/// (`frbtc_outpoints`, same `[outpoint, sats]` shape as sell_outpoints)
	/// and `carried_frbtc` names the frBTC they contribute. The caller MUST
	/// verify the returned `swapAmountIn` equals mint + carried before
	/// signing — an older API silently ignores the fields, returns
	/// `swapAmountIn: null`, and its chain would under-fund the quote.
	public static async getAlkaneWrapSwap(
		amountSats: number,
		path: string[],
		minOut: bigint,
		fee_rate: number,
		pools: [string, boolean][] = [],
		topup: { outpoints: [string, number][]; carriedFrbtc: bigint } | null = null
	): Promise<{
		wrapPsbt: string
		swapPsbt: string
		wrapTxid: string
		swapTxid: string
		wrapFee: bigint
		swapFee: bigint
		frbtcMinted: bigint
		swapAmountIn: bigint | null
		signerAddress: string
	}> {
		const payload: any = {
			fee_rate,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			outpoint_public_key: null,
			amount_sats: amountSats,
			path,
			pools
		}
		if (topup) {
			payload.frbtc_outpoints = topup.outpoints
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.outpoint_public_key = Wallet.taprootPaymentPublicKey()
		}

		// min_out / carried_frbtc are u128 — same bare-integer embedding as
		// getAlkaneSwap.
		const body = JSON.stringify(
			topup
				? { ...payload, min_out: '__MIN__', carried_frbtc: '__CARRY__' }
				: { ...payload, min_out: '__MIN__' }
		)
			.replace('"__MIN__"', minOut.toString())
			.replace('"__CARRY__"', topup ? topup.carriedFrbtc.toString() : '0')

		const { data } = await axios.post(`${API_URL}/alkane/wrap-swap`, body, {
			headers: { 'Content-Type': 'application/json' }
		})

		return {
			wrapPsbt: data.wrap_psbt,
			swapPsbt: data.swap_psbt,
			wrapTxid: data.wrap_txid,
			swapTxid: data.swap_txid,
			wrapFee: Wallet.getPSBTFee(data.wrap_psbt),
			swapFee: Wallet.getPSBTFee(data.swap_psbt),
			frbtcMinted: BigInt(data.frbtc_minted),
			// The capability echo: only the top-up-aware API sends this.
			swapAmountIn: data.swap_amount_in != null ? BigInt(data.swap_amount_in) : null,
			signerAddress: data.signer_address
		}
	}

	/// Wrap BTC → frBTC via the federation signer (0.1% fee). frBTC lands on
	/// the v0 dust output after one confirmation.
	public static async getAlkaneWrap(
		amountSats: number,
		fee_rate: number
	): Promise<{ psbt: string; fee: bigint; frbtcMinted: number; signerAddress: string }> {
		const payload: any = {
			fee_rate,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			outpoint_public_key: null,
			// u64 on the API — sats fit comfortably in a JSON Number.
			amount_sats: amountSats
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.outpoint_public_key = Wallet.taprootPaymentPublicKey()
		}

		const { data } = await axios.post(`${API_URL}/alkane/wrap`, payload)
		const fee = Wallet.getPSBTFee(data.psbt)

		return {
			psbt: data.psbt,
			fee,
			frbtcMinted: data.frbtc_minted,
			signerAddress: data.signer_address
		}
	}

	/// Unwrap frBTC → BTC. The signer settles asynchronously — BTC arrives
	/// after the federation processes the burn.
	public static async getAlkaneUnwrap(
		amountRaw: bigint,
		outpoints: [string, number][],
		fee_rate: number
	): Promise<{ psbt: string; fee: bigint; note?: string }> {
		const payload: any = {
			fee_rate,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			outpoint_public_key: null,
			sell_outpoints: outpoints
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.outpoint_public_key = Wallet.taprootPaymentPublicKey()
		}

		// amount is u128 on the API — embed as a bare JSON integer (same
		// technique as getAlkaneSwap: BigInt can exceed Number.MAX_SAFE_INTEGER).
		const body = JSON.stringify({ ...payload, amount: '__AMT__' }).replace(
			'"__AMT__"',
			amountRaw.toString()
		)

		const { data } = await axios.post(`${API_URL}/alkane/unwrap`, body, {
			headers: { 'Content-Type': 'application/json' }
		})
		const fee = Wallet.getPSBTFee(data.psbt)

		return { psbt: data.psbt, fee, note: data.note }
	}

	public static async getRunePurchases(
		outpoints: string[],
		fee_rate: number
	): Promise<{
		psbt: string
		rebar: boolean
		purchase_fee: bigint
		market_fee: number
		rebar_fee: number
	}> {
		await ensureEccLib()
		assertLegacyPurchaseBlocked(outpoints)

		const payload: any = {
			outpoints: outpoints,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			to: undefined,
			fee_rate,
			wallet_type: Wallet.type() || undefined
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.to = Wallet.getTaprootAddress()
		}

		const { data } = await axios.post(`${API_URL}/wallet/purchase-bulk-runes`, payload)

		const rebar = data.rebar === true
		const rebar_fee = rebar ? await Wallet.getRebarFee(data.psbt) : 0
		const purchase_fee = rebar ? BigInt(0) : Wallet.getPSBTFee(data.psbt)
		const market_fee = Wallet.getMarketFee(data.psbt)
		console.log(
			`[wallet.getRunePurchases] rebar=${rebar} purchaseFee=${purchase_fee.toString()} rebarFee=${rebar_fee} marketFee=${market_fee}`
		)

		return { ...data, purchase_fee, market_fee, rebar_fee }
	}

	public static async getAlkanePurchases(
		outpoints: string[],
		fee_rate: number
	): Promise<{
		psbt: string
		rebar: boolean
		purchase_fee: bigint
		market_fee: number
		rebar_fee: number
	}> {
		await ensureEccLib()
		assertLegacyPurchaseBlocked(outpoints)

		const payload: any = {
			outpoints: outpoints,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			to: undefined,
			fee_rate,
			wallet_type: Wallet.type() || undefined
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.to = Wallet.getTaprootAddress()
		}

		const { data } = await axios.post(`${API_URL}/wallet/purchase-bulk-alkanes`, payload)

		const rebar = data.rebar === true
		const rebar_fee = rebar ? await Wallet.getRebarFee(data.psbt) : 0
		const purchase_fee = rebar ? BigInt(0) : Wallet.getPSBTFee(data.psbt)
		const market_fee = Wallet.getMarketFee(data.psbt)
		console.log(
			`[wallet.getAlkanePurchases] rebar=${rebar} purchaseFee=${purchase_fee.toString()} rebarFee=${rebar_fee} marketFee=${market_fee}`
		)

		return { ...data, purchase_fee, market_fee, rebar_fee }
	}

	public static async getPurchases(
		inscription_ids: string[],
		fee_rate: number
	): Promise<{
		setup: string
		purchase: string
		rebar: boolean
		setup_fee: bigint
		purchase_fee: bigint
		market_fee: number
		rebar_fee: number
	}> {
		await ensureEccLib()
		await Wallet.provider.assertActiveSession?.({ interactive: false })
		assertLegacyPurchaseBlocked(inscription_ids)

		const payload: any = {
			inscriptions: inscription_ids,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			to: undefined,
			fee_rate: fee_rate,
			wallet_type: Wallet.type() || undefined
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.to = Wallet.getTaprootAddress()
		}

		const { data } = await axios.post(`${API_URL}/wallet/purchase-bulk`, payload)

		const rebar = data.rebar === true
		const setup_fee = data.setup ? Wallet.getPSBTFee(data.setup) : BigInt(0)
		const rebar_fee = rebar ? await Wallet.getRebarFee(data.purchase) : 0
		const purchase_fee = rebar ? BigInt(0) : Wallet.getPSBTFee(data.purchase)
		const market_fee = Wallet.getMarketFee(data.purchase)
		console.log(
			`[wallet.getPurchases] rebar=${rebar} setupFee=${setup_fee.toString()} purchaseFee=${purchase_fee.toString()} rebarFee=${rebar_fee} marketFee=${market_fee}`
		)

		return { ...data, setup_fee, purchase_fee, market_fee, rebar_fee }
	}

	public static async getPurchase(
		inscription_id: string,
		fee_rate: number = 10
	): Promise<{
		setup: string
		purchase: string
		rebar: boolean
		setup_fee: bigint
		purchase_fee: bigint
		market_fee: number
		rebar_fee: number
	}> {
		await ensureEccLib()
		await Wallet.provider.assertActiveSession?.({ interactive: false })
		assertLegacyPurchaseBlocked([inscription_id])

		const payload: any = {
			inscription: inscription_id,
			from: Wallet.getTaprootAddress(),
			public_key: Wallet.getPublicKey(),
			to: undefined,
			fee_rate
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			payload.from = Wallet.balanceAddress()
			payload.to = Wallet.getTaprootAddress()
		}

		const { data } = await axios.post(`${API_URL}/wallet/purchase`, payload)

		const rebar = data.rebar === true
		const setup_fee = data.setup ? Wallet.getPSBTFee(data.setup) : BigInt(0)
		const rebar_fee = rebar ? await Wallet.getRebarFee(data.purchase) : 0
		const purchase_fee = rebar ? BigInt(0) : Wallet.getPSBTFee(data.purchase)
		const market_fee = Wallet.getMarketFee(data.purchase)
		console.log(
			`[wallet.getPurchase] rebar=${rebar} setupFee=${setup_fee.toString()} purchaseFee=${purchase_fee.toString()} rebarFee=${rebar_fee} marketFee=${market_fee}`
		)

		return { ...data, setup_fee, purchase_fee, market_fee, rebar_fee }
	}

	public static async getMintInscribe(
		hash: string,
		content_type: string,
		feeRate: number,
		postage: number
	): Promise<string> {
		let from = Wallet.getTaprootAddress()
		let to = Wallet.getTaprootAddress()
		let payload_postage = postage

		// Any payments wallet (Xverse, OYL, Leather) funds the commit from its
		// payment address; this used to be gated on Xverse alone, so OYL and
		// Leather commits were built against an address that holds no BTC.
		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			from = Wallet.balanceAddress()
			payload_postage = 1000
		}

		const url = `${API_MINT_URL}/mint/hash-match/upload`
		const { data } = await axios.post(`${url}`, {
			hash,
			content_type,
			postage: payload_postage,
			destination: to,
			fee_rate: feeRate,
			from,
			public_key: Wallet.getPublicKey()
		})

		return data.psbt
	}

	public static async getRandomMintInscribe(
		slug: string,
		feeRate: number,
		postage: number
	): Promise<string> {
		let from = Wallet.getTaprootAddress()
		let to = Wallet.getTaprootAddress()
		let payload_postage = postage

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			from = Wallet.balanceAddress()
			payload_postage = 1000
		}

		const url = `${API_MINT_URL}/mint/${slug}/hash-match/random`
		const { data } = await axios.post(`${url}`, {
			postage: payload_postage,
			destination: to,
			fee_rate: feeRate,
			from,
			public_key: Wallet.getPublicKey()
		})

		return data.psbt
	}

	public static async getGalleryInscribe(
		file: File | null,
		parentInscriptionId: string,
		parentOutpoint: string,
		parentUtxoValue: number,
		parentReturnAddress: string,
		properties: object,
		delegateInscriptionId: string | null,
		feeRate: number,
		postage: number,
		metadata?: object
	): Promise<{ psbt: string; reveal_psbt: string }> {
		const formData = new FormData()
		if (file) {
			formData.append('file', file)
		} else {
			// Minimal placeholder when using delegate
			const placeholder = new Blob([new Uint8Array([0])], { type: 'application/octet-stream' })
			formData.append('file', placeholder, 'gallery.bin')
		}

		let from = Wallet.getTaprootAddress()
		let to = Wallet.getTaprootAddress()
		let payload_postage = postage

		if (Wallet.provider?.capabilities?.includes('payments-wallet')) {
			from = (Wallet as any)?.provider?.balanceAddress()
			payload_postage = 1000
		}

		let url = `${INSCRIBE_API_URL}/inscribe/upload`
		url = `${url}?postage=${payload_postage}&destination=${to}&fee_rate=${feeRate}&from=${from}&public_key=${Wallet.getPublicKey()}`
		if (parentInscriptionId) {
			url = `${url}&parent_inscription_id=${encodeURIComponent(parentInscriptionId)}`
			url = `${url}&parent_outpoint=${encodeURIComponent(parentOutpoint)}`
			url = `${url}&parent_utxo_value=${parentUtxoValue}`
			url = `${url}&parent_return_address=${encodeURIComponent(parentReturnAddress)}`
		}
		if (delegateInscriptionId) {
			url = `${url}&delegate_inscription_id=${encodeURIComponent(delegateInscriptionId)}`
		}

		// Gzip large properties to reduce upload size for big collections
		const propertiesStr = JSON.stringify(properties)
		const propertiesBytes = new TextEncoder().encode(propertiesStr).length
		if (propertiesBytes > 100_000) {
			const stream = new Blob([propertiesStr]).stream()
			const compressed = stream.pipeThrough(new CompressionStream('gzip'))
			const buf = await new Response(compressed).arrayBuffer()
			const bytes = new Uint8Array(buf)
			let binary = ''
			for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
			formData.append('properties_gz', btoa(binary))
		} else {
			formData.append('properties', propertiesStr)
		}
		if (metadata) {
			formData.append('metadata', JSON.stringify(metadata))
		}

		const { data } = await axios.post(url, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})

		return { psbt: data.psbt, reveal_psbt: data.reveal_psbt }
	}

	public static async getInscribe(
		files: { file: File }[],
		feeRate: number,
		postage: number
	): Promise<string> {
		if (!files?.length || !files[0]?.file) {
			throw new Error('No file selected for inscription')
		}

		let formData = new FormData()
		formData.append('file', files[0].file)

		let from = Wallet.getTaprootAddress()
		let to = Wallet.getTaprootAddress()

		let payload_postage = postage

		if (Wallet.provider?.capabilities?.includes('payments-wallet')) {
			from = (Wallet as any)?.provider?.balanceAddress()
			payload_postage = 1000
		}

		let url = `${API_URL}/inscribe/upload`
		url = `${url}?postage=${payload_postage}&destination=${to}&fee_rate=${feeRate}&from=${from}&public_key=${Wallet.getPublicKey()}`

		if (files?.length >= 2 && files[1]?.file) {
			if (files[0].file.name === files[1].file.name) {
				url = `${url}&copies=${files?.length}`
			} else {
				formData = new FormData()
				files.forEach((e, i) => {
					if (e?.file) formData.append(`file-${i}`, e.file)
				})
			}
		}

		const { data } = await axios.post(url, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})

		return data.psbt
	}

	public static async signMessage(
		message: string
	): Promise<{ signature?: string; success: boolean }> {
		const type = Wallet.type()
		if (type === 'ordinalswallet') {
			const seed = await Wallet.seed()
			if (!seed.valid) {
				return { success: false }
			}
			const { signMessage } = await import('@/utils/bitcoin')
			const r = await signMessage(seed.seed, message)
			return { signature: r, success: true }
		} else {
			const r = await Wallet.provider.signMessage(message)
			return { signature: r, success: true }
		}
	}

	public static async signPSBTS(psbts: string[]): Promise<{ psbts?: string[]; success: boolean }> {
		const type = Wallet.type()

		let response: string[] = []
		if (type === 'ordinalswallet') {
			for (let psbt of psbts) {
				const { psbt: signedPsbt } = await this.signPSBT(psbt)
				response.push(signedPsbt as any)
			}
		} else {
			if (Wallet.provider.signPSBTS) {
				response = await Wallet.runExternalSigningRequest('sign-psbts', () =>
					(Wallet as any).provider.signPSBTS(psbts)
				)
			} else {
				for (let psbt of psbts) {
					const signedPsbt = await Wallet.runExternalSigningRequest('sign-psbt', () =>
						(Wallet as any).provider.signPSBT(psbt)
					)
					response.push(signedPsbt as any)
				}
			}
		}

		return { psbts: response, success: true }
	}

	public static async signPSBTSExtractTx(psbts: string[]): Promise<string[]> {
		if (Wallet.type() === 'ordinalswallet') openPasswordModalForGesture()
		await ensureEccLib()

		try {
			// @ts-ignore
			const { psbts: data } = await this.signPSBTS(psbts)
			return (data as string[]).map((psbt) => extractFinalizedPsbtHex(psbt))
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	public static async signPSBT(psbt: string): Promise<{ psbt?: string; success: boolean }> {
		if (Wallet.type() === 'ordinalswallet') openPasswordModalForGesture()
		await ensureEccLib()

		const type = Wallet.type()
		let res

		if (type === 'ordinalswallet') {
			const seed = await Wallet.seed()
			if (!seed.valid) {
				return { success: false }
			}

			const { signRawtx } = await import('@/utils/bitcoin')
			res = await signRawtx(seed.seed, { psbt, disableExtract: true })
		} else {
			res = await Wallet.runExternalSigningRequest('sign-psbt', () =>
				(Wallet as any).provider.signPSBT(psbt)
			)
		}

		return { psbt: res as any, success: true }
	}

	public static async signPSBTExtractTx(psbt: string): Promise<string> {
		if (Wallet.type() === 'ordinalswallet') openPasswordModalForGesture()
		await ensureEccLib()

		try {
			// @ts-ignore
			const { psbt: data } = await this.signPSBT(psbt)
			return extractFinalizedPsbtHex(data as string)
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	public static async signInscribe(psbt: string): Promise<{ rawtx?: string; success: boolean }> {
		const type = Wallet.type()
		let rawtx

		if (type === 'ordinalswallet') {
			rawtx = await this.signPSBTExtractTx(psbt)
		} else {
			rawtx = await Wallet.runExternalSigningRequest('sign-inscribe', () =>
				Wallet.provider.signInscribe(psbt)
			)
		}

		return { rawtx, success: true }
	}

	public static async signMintInscribe(
		psbt: string
	): Promise<{ rawtx?: string; success: boolean }> {
		const type = Wallet.type()
		let rawtx

		if (type === 'ordinalswallet') {
			rawtx = await this.signPSBTExtractTx(psbt)
		} else if (type === 'xverse') {
			rawtx = await Wallet.runExternalSigningRequest('sign-mint-inscribe', () =>
				Wallet.xverse.signMintInscribe(psbt)
			)
		} else {
			rawtx = await Wallet.runExternalSigningRequest('sign-inscribe', () =>
				Wallet.provider.signInscribe(psbt)
			)
		}

		return { rawtx, success: true }
	}

	public static async signPurchase(
		setup_psbt: string,
		purchase_psbt: any
	): Promise<{ signedSetup?: string; signedPurchase?: string; success: boolean }> {
		if (Wallet.type() === 'ordinalswallet') openPasswordModalForGesture()
		await ensureEccLib()

		const type = Wallet.type()
		if (type === 'ordinalswallet') {
			let signedSetup = ''
			let purchase = purchase_psbt

			if (setup_psbt) {
				const { psbt: signedSetupPSBT } = await this.signPSBT(setup_psbt)
				signedSetup = extractFinalizedPsbtHex(signedSetupPSBT as string)
				// Re-point the purchase at the setup's SIGNED txid and carry the
				// signed setup as its parent (chain.ts owns this).
				const stale = inputTxidHex(parsePsbtTransaction(purchase_psbt), 0)
				purchase = linkChildToSignedParent(signedSetup, purchase_psbt, [stale])
			}

			const { psbt: signedPurchase } = await this.signPSBT(purchase)
			return { signedSetup, signedPurchase, success: true }
		} else {
			const result = await Wallet.runExternalSigningRequest('sign-purchase', () =>
				Wallet.provider.signPurchase(setup_psbt, purchase_psbt)
			)

			if (!result || (setup_psbt && !result.signedSetup) || !result.signedPurchase) {
				return { success: false }
			}

			// Providers that do not re-point the purchase at the signed setup
			// (UniSat, OKX, OYL, Tap, Binance, Phantom) hand back a purchase
			// that still references the server's pre-sign setup id. That only
			// matches for native-segwit and taproot funders; for anything else
			// the purchase would spend an outpoint that never exists. Refuse
			// here, before /market/purchase sees it.
			if (setup_psbt && result.signedSetup) {
				assertChainLinks('purchase', result.signedSetup, result.signedPurchase)
			}

			return { ...result, success: true }
		}
	}

	public static async signTransfer(
		psbt: string,
		inscriptions: number
	): Promise<{ rawtx?: string; success: boolean }> {
		const type = Wallet.type()
		let rawtx

		if (type === 'ordinalswallet') {
			rawtx = await this.signPSBTExtractTx(psbt)
		} else {
			rawtx = await Wallet.runExternalSigningRequest('sign-transfer', () =>
				(Wallet as any).provider.signTransfer(psbt, inscriptions)
			)
		}

		return { rawtx: rawtx as any, success: true }
	}

	public static async signOffer(psbt: string): Promise<{ psbt?: string; success: boolean }> {
		return Wallet.signPSBT(psbt)
	}

	public static async signEscrow(psbt: string): Promise<{ rawtx?: string; success: boolean }> {
		const type = Wallet.type()
		let rawtx

		if (type === 'ordinalswallet') {
			let { psbt: data } = await this.signPSBT(psbt)
			rawtx = data
		} else {
			rawtx = await Wallet.runExternalSigningRequest('sign-escrow', () =>
				Wallet.provider.signEscrow(psbt)
			)
		}

		return { rawtx, success: true }
	}

	private static runExternalSigningRequest<T>(
		label: string,
		request: () => Promise<T>
	): Promise<T> {
		return runExternalSigningRequest(label, request)
	}

	public static getPublicKey(): string {
		if (Wallet.notDefined()) {
			return ''
		}

		return SafeLocalStorage.getItem(STORAGE_PUBLIC_KEY)
	}

	public static balanceAddress(): string {
		if (Wallet.notDefined()) {
			return ''
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			return Wallet.provider.balanceAddress?.() || ''
		}

		return SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
	}

	public static balancePublicKey(): string {
		if (Wallet.notDefined()) {
			return ''
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			return Wallet.provider.balancePublicKey?.() || ''
		}

		return SafeLocalStorage.getItem(STORAGE_PUBLIC_KEY)
	}

	public static taprootPaymentPublicKey(): string {
		if (Wallet.notDefined()) {
			return ''
		}

		if (Wallet.provider.capabilities.includes('payments-wallet')) {
			return Wallet.provider.taprootPublicKey?.() || ''
		}

		return SafeLocalStorage.getItem(STORAGE_PUBLIC_KEY)
	}

	public static getTaprootAddress(): string {
		if (Wallet.notDefined()) {
			return ''
		}

		return SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
	}

	public static getItem(key: string): string {
		if (Wallet.notDefined()) {
			return ''
		}

		return SafeLocalStorage.getItem(key)
	}

	/** What each API skip reason means to a seller. */
	static readonly ESCROW_SKIP_REASONS: Record<string, string> = {
		pending_sale: 'already sold, its sale is waiting to be mined',
		escrow_price_below_dust_limit: 'price is below the dust limit',
		ord_output_missing: 'not found by the indexer yet',
		no_supported_assets_on_output: 'holds nothing listable',
		missing_final_script: 'not signed',
		invalid_seller_output_script: 'payout script is not yours',
		script_verification_failed: 'signature did not verify'
	}

	public static formatEscrowSkip(skip: any): string {
		const index = typeof skip?.input_index === 'number' ? skip.input_index + 1 : '?'
		const reason = skip?.reason || 'unknown_reason'
		const why = Wallet.ESCROW_SKIP_REASONS[reason] ?? reason
		return `lot ${index}: ${why}`
	}

	public static formatEscrowSkips(skips: any[], max: number = 5): string {
		if (!Array.isArray(skips) || skips.length === 0) {
			return ''
		}

		const lines = skips.slice(0, max).map((s) => Wallet.formatEscrowSkip(s))
		const suffix = skips.length > max ? ` (+${skips.length - max} more)` : ''
		return `${lines.join(', ')}${suffix}`
	}

	public static assertEscrowResponse(data: any): {
		createdEscrows: number
		skippedInputsCount: number
		skippedInputs: any[]
		skippedSummary: string
	} {
		const failed =
			data?.success === false || data?.status === 'error' || data?.error || data?.result === false

		if (failed) {
			const message = data?.message || data?.error || 'Listing failed'
			throw new Error(message)
		}

		const skippedInputs = Array.isArray(data?.skipped_inputs) ? data.skipped_inputs : []
		const skippedInputsCountRaw = Number(data?.skipped_inputs_count)
		const skippedInputsCount = Number.isNaN(skippedInputsCountRaw)
			? skippedInputs.length
			: skippedInputsCountRaw

		const createdEscrowsRaw = Number(data?.created_escrows)
		const createdEscrows = Number.isNaN(createdEscrowsRaw)
			? Array.isArray(data?.escrows)
				? data.escrows.length
				: 1
			: createdEscrowsRaw

		const skippedSummary = Wallet.formatEscrowSkips(skippedInputs)

		if (createdEscrows <= 0) {
			const baseMessage = data?.message || 'No escrows were created'
			const message = skippedSummary ? `${baseMessage}. Skipped: ${skippedSummary}` : baseMessage
			throw new Error(message)
		}

		return {
			createdEscrows,
			skippedInputsCount,
			skippedInputs,
			skippedSummary
		}
	}

	public static handleError(e: any) {
		console.log('WALLET_ERROR', e)
		const message = e?.response?.data?.message ? e.response.data.message : 'Wallet error'
		event.emit('snackbar', { variant: 'error', title: message })
		reportWalletError(e)
	}

	public static setItem(key: string, value: string) {
		if (Wallet.notDefined()) {
			return
		}

		SafeLocalStorage.setItem(key, value)
	}

	private static notDefined() {
		return typeof window === 'undefined'
	}
}
